/**
 * src/lib/hexclave.ts — the Hexclave client app. The app's only source of identity.
 *
 * Plan: `docs/PLAN-STACKAUTH.md` §7 (frontend), §7.0 (which token to send), §10 step 3.
 *
 * `VITE_AUTH_MODE` and the `IS_HEXCLAVE_MODE` flag were deleted on 2026-08-09 (owner instruction)
 * together with the legacy password login they used to select between. There is one auth path now,
 * so a flag with one valid value would only be a way to get it wrong.
 */
import { HexclaveClientApp } from '@hexclave/react'

/**
 * The project id. Public by design — it is the JWT `aud`, a path segment of the public JWKS URL, and
 * it ships to every browser regardless. Falls back to the production project so a local `npm run
 * dev:client` works without a `.env` file.
 */
const PROJECT_ID =
  (import.meta.env.VITE_HEXCLAVE_PROJECT_ID as string | undefined) ??
  'de3b18c3-af56-425a-866a-ebb721570237'

/**
 * The client app.
 *
 * `tokenStore: 'cookie'` is the documented setting for non-Next web frontends. Note this cookie is
 * the SDK's **own session storage on the Pages origin** — it never travels to the Worker, which lives
 * on a different host and is handed the token explicitly in a header (see {@link getHexclaveToken}).
 * So the app and API being cross-site is not a problem for it.
 *
 * ═══ `handler-component`, NOT `hosted` — READ BEFORE CHANGING ══════════════════════════════════
 * Both settings give the same maintained sign-in, sign-up, **password reset** and email-verification
 * flows. The difference is *where the user's browser is while using them*:
 *
 *   `hosted`             → https://<project-id>.built-with-hexclave.com/handler/sign-in?…
 *   `handler-component`  → https://l2hc.pages.dev/handler/sign-in
 *
 * `hosted` was the original choice and it was **wrong for this app** (owner, 2026-08-09): it sends
 * translators to a vendor-branded domain carrying the project id in the hostname, and announces to
 * anyone signing in which auth provider the project uses. Nothing *confidential* leaks — the project
 * id is the public `aud`, a path segment of the public JWKS URL, and it already ships in this bundle
 * — but the app should not hand its users off to somebody else's domain to log in.
 *
 * `handler-component` renders the same SDK components from **our** origin instead. It requires the
 * `/handler/*` route in `src/App.tsx`; the two must be changed together, or every auth link 404s.
 *
 * Note the vendor's quickstart recommends `hosted` for new projects, and the type's own doc comment
 * calls `handler-component` "current default, may change in the next breaking version". Pin it
 * explicitly, as here, rather than relying on either default.
 *
 * Constructed lazily. `HexclaveClientApp` prefetches on construction, so deferring it keeps that
 * network work out of module evaluation and lets tests import this file without opening a connection.
 */
let cached: ReturnType<typeof createApp> | null = null

function createApp() {
  return new HexclaveClientApp({
    projectId: PROJECT_ID,
    tokenStore: 'cookie',
    // See the header. Every auth page renders on this origin, at /handler/*.
    urls: { default: { type: 'handler-component' } },
  })
}

export function hexclaveApp(): ReturnType<typeof createApp> {
  cached ??= createApp()
  return cached
}

/**
 * The **raw** access token for the current user, or `null` when signed out.
 *
 * ═══ READ THIS BEFORE CHANGING IT ══════════════════════════════════════════════════════════════
 * `getAccessToken()` returns the raw JWT (`eyJ…`), which is what the Worker verifies against
 * Hexclave's public JWKS.
 *
 * **Do NOT switch this to `getAuthorizationHeader()`.** That returns `Bearer stackauth_eyJ…` — a
 * scheme *and* a `stackauth_` prefix — because it is built for `HexclaveServerApp.getUser()`, the
 * path that needs the secret server key. `jose` cannot verify it, and the failure surfaces as a
 * *signature* error that reads like a key or JWKS problem and is not (plan §7.0). The Worker's
 * `extractHexclaveToken` rejects that form on sight precisely so the symptom stays honest.
 *
 * Returns `null` rather than throwing when signed out: `getAuthHeadersAsync` then omits the header
 * and the Worker answers its ordinary 401.
 */
export async function getHexclaveToken(): Promise<string | null> {
  try {
    const user = await hexclaveApp().getUser()
    if (!user) return null
    return await user.getAccessToken()
  } catch {
    // Never let a token fetch throw into a data hook: the app has no global error handling
    // (spec §2.4, DF-22 rank 5), so a rejection here would surface as an unhandled promise rather
    // than the 401 the caller can already handle.
    return null
  }
}
